import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Receipt, Wallet, Landmark } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type {
  Lancamento,
  ResumoCreditos,
  TipoLancamento,
} from '../../api/tipos';
import {
  Aviso,
  Cartao,
  Celula,
  Esqueleto,
  Etiqueta,
  Indicador,
  Indicadores,
  Paginacao,
  Tabela,
  Td,
  Th,
  Vazio,
  cx,
  type TomEtiqueta,
} from '../../componentes/ui';
import { dataHora, numero, plural, reais } from '../../util/formato';
import { RecarregarPix } from './RecarregarPix';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import css from './Creditos.module.css';

/** Linhas do extrato por página — o extrato vem inteiro da API. */
const POR_PAGINA = 20;

/** Como cada movimento aparece no extrato. */
const LANCAMENTO: Record<
  TipoLancamento,
  { rotulo: string; sinal: '+' | '−' | ''; tom: TomEtiqueta }
> = {
  RECARGA: { rotulo: 'Recarga', sinal: '+', tom: 'ok' },
  ESTORNO: { rotulo: 'Devolução', sinal: '+', tom: 'ok' },
  RESERVA: { rotulo: 'Reserva', sinal: '−', tom: 'alerta' },
  CONSUMO: { rotulo: 'Consumo', sinal: '', tom: 'neutro' },
  AJUSTE: { rotulo: 'Ajuste', sinal: '+', tom: 'info' },
  BONUS: { rotulo: 'Bônus', sinal: '+', tom: 'ok' },
};

export function Creditos() {
  const [resumo, setResumo] = useState<ResumoCreditos | null>(null);
  const [extrato, setExtrato] = useState<Lancamento[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([
        api<ResumoCreditos>('/creditos'),
        api<Lancamento[]>('/creditos/extrato'),
      ]);
      setResumo(r);
      setExtrato(e);
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Falha ao carregar os créditos.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const visiveis = extrato?.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA) ?? [];

  return (
    <Pagina>
      <TituloPagina
        titulo="Créditos"
        descricao="Você paga por XML entregue. O valor do lote fica reservado enquanto a busca roda, e o que não for baixado volta para o saldo."
      />

      {erro && <Aviso>{erro}</Aviso>}

      {!resumo ? (
        <Indicadores>
          <Esqueleto altura={104} raio={12} />
          <Esqueleto altura={104} raio={12} />
          <Esqueleto altura={104} raio={12} />
        </Indicadores>
      ) : (
        <>
          <Indicadores>
            <Indicador
              rotulo="Disponível"
              valor={reais(resumo.saldoCentavos)}
              detalhe="Pode ser usado em novas buscas"
              icone={<Wallet />}
            />
            <Indicador
              rotulo="Reservado"
              valor={reais(resumo.reservadoCentavos)}
              detalhe={`Travado em ${plural(resumo.reservas.length, 'busca em andamento', 'buscas em andamento')}`}
              icone={<Lock />}
              tom={resumo.reservadoCentavos > 0 ? 'alerta' : undefined}
            />
            <Indicador
              rotulo="Total na conta"
              valor={reais(resumo.totalCentavos)}
              detalhe="Disponível + reservado"
              icone={<Landmark />}
            />
          </Indicadores>

          {resumo.reservas.length > 0 && (
            <Cartao
              padding={0}
              titulo="O que está reservado"
              descricao="Este valor volta ao saldo quando a busca terminar — descontado apenas o que foi realmente entregue."
            >
              <Tabela rotulo="Reservas em andamento">
                <thead>
                  <tr>
                    <Th>Busca</Th>
                    <Th alinhar="direita">Preço por XML</Th>
                    <Th alinhar="direita">Reservado</Th>
                  </tr>
                </thead>
                <tbody>
                  {resumo.reservas.map((r) => (
                    <tr key={r.id}>
                      <Td>
                        <Celula
                          para={`/painel/downloads/${r.jobId}`}
                          principal={plural(r.chavesReservadas, 'chave')}
                          secundaria={`Iniciada em ${dataHora(r.createdAt)}`}
                        />
                      </Td>
                      <Td alinhar="direita" className="num">
                        {reais(r.precoUnitarioCentavos)}
                      </Td>
                      <Td alinhar="direita">
                        <span className={cx('num', css.reservado)}>
                          {reais(r.valorReservadoCentavos)}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Tabela>
            </Cartao>
          )}

          {/* Em tela larga os dois ficam lado a lado; abaixo disso o grid
              colapsa sozinho, sem media query. */}
          <Colunas minimo={440} alinhar="start">
            <RecarregarPix aoConfirmar={() => void carregar()} />
            <TabelaDePrecos resumo={resumo} />
          </Colunas>
        </>
      )}

      <Cartao
        padding={0}
        titulo="Extrato"
        descricao="Recargas, reservas e consumos, do mais recente ao mais antigo."
      >
        {!extrato ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : extrato.length === 0 ? (
          <Vazio
            icone={<Receipt />}
            titulo="Nenhum movimento ainda"
            descricao="Recargas, reservas e consumos aparecem aqui assim que acontecerem."
          />
        ) : (
          <>
            <Tabela rotulo="Extrato de créditos">
              <thead>
                <tr>
                  <Th>Data</Th>
                  <Th>Tipo</Th>
                  <Th>Descrição</Th>
                  <Th alinhar="direita">Valor</Th>
                  <Th alinhar="direita">Saldo após</Th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((l) => {
                  const cfg = LANCAMENTO[l.tipo];
                  return (
                    <tr key={l.id}>
                      <Td>
                        <span className={cx('num', css.data)}>{dataHora(l.createdAt)}</span>
                      </Td>
                      <Td>
                        <Etiqueta tom={cfg.tom}>{cfg.rotulo}</Etiqueta>
                      </Td>
                      <Td className={css.descricao}>
                        {l.descricao}
                        {l.jobId && (
                          <>
                            {' · '}
                            <Link to={`/painel/downloads/${l.jobId}`}>ver busca</Link>
                          </>
                        )}
                      </Td>
                      <Td alinhar="direita">
                        <span
                          className={cx(
                            'num',
                            css.valor,
                            cfg.sinal === '+' && css.entrada,
                            cfg.sinal === '−' && css.saida,
                          )}
                        >
                          {cfg.sinal} {reais(l.valorCentavos)}
                        </span>
                      </Td>
                      <Td alinhar="direita">
                        <span className={cx('num', css.saldo)}>
                          {reais(l.saldoDepoisCentavos)}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
            <Paginacao
              total={extrato.length}
              pagina={pagina}
              porPagina={POR_PAGINA}
              aoMudar={setPagina}
              rotulo={extrato.length === 1 ? 'movimento' : 'movimentos'}
            />
          </>
        )}
      </Cartao>
    </Pagina>
  );
}

function TabelaDePrecos({ resumo }: { resumo: ResumoCreditos }) {
  return (
    <Cartao
      padding={0}
      titulo="Preço por volume"
      descricao="O lote inteiro sai pelo preço da faixa em que ele cai — e o preço é travado no momento em que a busca começa, então não muda no meio."
      rodape={
        <span className={css.notaRodape}>
          Chave não encontrada na fonte <strong>não é cobrada</strong> — só XML efetivamente
          entregue.
        </span>
      }
    >
      <Tabela rotulo="Tabela de preços">
        <thead>
          <tr>
            <Th>Tamanho do lote</Th>
            <Th alinhar="direita">Preço por XML</Th>
          </tr>
        </thead>
        <tbody>
          {resumo.tabelaPrecos.map((f) => (
            <tr key={f.de}>
              <Td className="num">
                {f.ate === null
                  ? `Acima de ${numero(f.de - 1)} XMLs`
                  : `${numero(f.de)} a ${numero(f.ate)} XMLs`}
              </Td>
              <Td alinhar="direita" className={cx('num', css.preco)}>
                {reais(f.precoCentavos)}
              </Td>
            </tr>
          ))}
        </tbody>
      </Tabela>
    </Cartao>
  );
}
